import React, { useState, useEffect } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '../utils/api';
import { useGeolocation } from '../hooks/useGeolocation';

export default function CheckInPage() {
  const geo = useGeolocation();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus]   = useState('idle');  // idle|verifying|saving|success|error
  const [message, setMessage] = useState('');
  const [result, setResult]   = useState(null);
  const [clock, setClock]     = useState(new Date());

  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { api.getEmployees().then(setEmployees).catch(() => {}); }, []);

  async function handleCheckIn() {
    if (!employeeId) return err('Please select your name.');
    if (geo.error)   return err(geo.error);
    if (geo.loading) return err('Waiting for GPS…');
    try {
      setStatus('verifying'); setMessage('');
      const options    = await api.authOptions(employeeId);
      const credential = await startAuthentication(options);
      await api.authVerify(credential);
      setStatus('saving');
      const data = await api.checkIn({ employeeId, latitude: geo.lat, longitude: geo.lng, deviceInfo: navigator.userAgent.slice(0, 200) });
      setResult(data); setStatus('success');
    } catch (e) {
      const m = e.message || '';
      if (m.includes('wrong_location'))      err('You are not at an approved work location.');
      else if (m.includes('already_checked')) err('You have already checked in today.');
      else if (m.includes('No biometric'))   err('No biometric registered. Go to Register tab first.');
      else if (m.includes('NotAllowed'))     err('Fingerprint/Face ID was cancelled. Please try again.');
      else err(m || 'Something went wrong.');
    }
  }

  function err(msg) { setMessage(msg); setStatus('error'); }
  function reset()  { setStatus('idle'); setMessage(''); setResult(null); }

  const busy = status === 'verifying' || status === 'saving';

  if (status === 'success' && result) return (
    <Centered>
      <div className="card" style={{ maxWidth: 420, width: '100%' }}>
        <div style={iconCircle('#f0fdf4')}><span style={{ fontSize: 30 }}>✅</span></div>
        <h2 style={S.h2}>Attendance recorded!</h2>
        <p style={S.sub}>Welcome, {result.employeeName}</p>
        <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 8 }}>
          <Row label="Date"     value={new Date(result.checkInTime).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' })} />
          <Row label="Time"     value={new Date(result.checkInTime).toLocaleTimeString()} />
          <Row label="Location" value={result.locationName} />
          <Row label="Distance" value={`${result.distanceM} m from office`} />
        </div>
        <button className="btn btn-secondary btn-block" style={{ marginTop: '1.25rem' }} onClick={reset}>Done</button>
      </div>
    </Centered>
  );

  return (
    <Centered>
      <div className="card" style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 4 }}>
            {clock.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-1px' }}>{clock.toLocaleTimeString()}</div>
        </div>

        <Label>Your name</Label>
        <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} style={{ marginBottom: '1rem' }}>
          <option value="">— Select your name —</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.name} · {e.department}</option>)}
        </select>

        <Label>Location</Label>
        <div style={{ marginBottom: '1.25rem', fontSize: 13 }}>
          {geo.loading
            ? <span style={{ color: 'var(--gray-400)' }}><span className="spinner spinner-dark" style={{ width: 12, height: 12 }} /> Detecting…</span>
            : geo.error
            ? <span className="badge badge-red">⚠ {geo.error}</span>
            : <span className="badge badge-green">📍 GPS ready ({geo.lat?.toFixed(4)}, {geo.lng?.toFixed(4)})</span>
          }
        </div>

        {status === 'error' && message && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{message}</div>}

        <button className="btn btn-primary btn-block" onClick={handleCheckIn} disabled={busy || geo.loading} style={{ fontSize: 15, padding: '12px 16px' }}>
          {busy
            ? <><div className="spinner" />{status === 'saving' ? 'Saving…' : 'Verifying biometric…'}</>
            : '🔐 Verify & Check In'}
        </button>
        <p style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>
          Your device will ask for fingerprint or Face ID. No biometric data leaves your device.
        </p>
      </div>
    </Centered>
  );
}

const S = {
  h2:  { textAlign: 'center', fontSize: 18, fontWeight: 600, marginBottom: 4 },
  sub: { textAlign: 'center', color: 'var(--gray-500)', fontSize: 13, marginBottom: '1rem' },
};
const Centered = ({ children }) => <div style={{ display:'flex', justifyContent:'center', padding:'2rem 1rem' }}>{children}</div>;
const Label    = ({ children }) => <div style={{ fontWeight:500, fontSize:12, color:'var(--gray-700)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:5 }}>{children}</div>;
const Row      = ({ label, value }) => (
  <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid #f3f4f6', fontSize:13 }}>
    <span style={{ color:'var(--gray-500)' }}>{label}</span>
    <span style={{ fontWeight:500 }}>{value}</span>
  </div>
);
const iconCircle = bg => ({ width:64, height:64, borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' });
